import type * as Blockly from "blockly";
import type { BlocklyHelper, BlocklyNesting } from "../core/helper.ts";
import type { DefineBlockOptions } from "../core/parser.ts";
import type { PluginBlockFileContent } from "../core/plugin-types.ts";

type ToolboxItemInfo = Blockly.utils.toolbox.ToolboxItemInfo;

export type RegisterPluginByTxtOptions = DefineBlockOptions & {
  pluginNameIsTaken?: (pluginName: string) => boolean;
  onMissingBlockId?: (blockText: string) => void;
};

export function extractBlockIdsFromPluginTxt(pluginTXT: string): string[] {
  return pluginTXT.split("###BLOCKDEF").map((block) => {
    const idMatch = block.match(/#::ID\s+(\S+)/);
    return idMatch ? idMatch[1] : null;
  }).filter((id): id is string => id !== null);
}

export function getPluginName(
  pluginTXT: string,
  pluginNameIsTaken: (pluginName: string) => boolean = () => false,
): string {
  const firstRow = pluginTXT.split("\n")[0]?.trim() ?? "";
  const baseName = firstRow.startsWith("@plugin:") ? firstRow.substring(8).trim() : firstRow;

  let name = baseName;
  let counter = 2;
  while (pluginNameIsTaken(name)) {
    name = `${baseName} (${counter})`;
    counter++;
  }
  return name;
}

export function getRawBlocks(pluginTXT: string): string[] {
  return pluginTXT
    .split("###BLOCKDEF")
    .filter((block) => !block.trim().startsWith("@plugin"))
    .map((block) => block.trim());
}

export function parseBlockFileToObject(text: string): PluginBlockFileContent {
  const lines = text.split("\n");
  const result: Partial<PluginBlockFileContent> = {};
  let currentKey: keyof PluginBlockFileContent | null = null;

  lines.forEach((line) => {
    if (line.startsWith("#::")) {
      currentKey = line.substring(3).trim() as keyof PluginBlockFileContent;
      result[currentKey] = "";
    } else if (currentKey) {
      const content = line.trim();
      if (content) {
        result[currentKey] = result[currentKey] ? `${result[currentKey]}\n${content}` : content;
      }
    }
  });

  return result as PluginBlockFileContent;
}

export function parsePluginNesting(blockData: PluginBlockFileContent): BlocklyNesting[] {
  const data = blockData.nestings;
  if (!data) return [];

  return data.split("\n").reduce((acc: BlocklyNesting[], line: string) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return acc;

    const match = trimmedLine.match(/^-([^:]+):([^|]+)\|(.*)/);
    if (!match) return acc;

    const [, forValue, type, rawValue] = match;
    if (!forValue || !type || rawValue === undefined) return acc;

    let parsedValue: string | number | boolean = rawValue;
    if (type === "NUMBER") parsedValue = Number(rawValue);
    if (type === "BOOLEAN") parsedValue = rawValue.toLowerCase() === "true";

    const typeToBlockType: Record<string, string> = {
      TEXT: "DUMMY_STRING",
      NUMBER: "DUMMY_NUMBER",
      BOOLEAN: "DUMMY_BOOLEAN",
    };
    const typeToBlockDefaults: Record<string, string> = {
      TEXT: "text",
      NUMBER: "number",
      BOOLEAN: "boolean",
    };

    const targetBlockType = typeToBlockType[type] ?? type;
    const targetBlockDefaultsKey = typeToBlockDefaults[type] ?? type.toLowerCase();

    acc.push({
      forValue,
      isShadow: true,
      targetBlockType,
      targetBlockDefaults: {
        [targetBlockDefaultsKey]: parsedValue,
      },
    });
    return acc;
  }, []);
}

export function registerPluginByTxt(
  helper: BlocklyHelper,
  fixedPluginTXT: string,
  options: RegisterPluginByTxtOptions = {},
): ToolboxItemInfo | null {
  const rawBlocks = getRawBlocks(fixedPluginTXT);
  const name = getPluginName(fixedPluginTXT, options.pluginNameIsTaken);
  const blocksArray: ToolboxItemInfo[] = [];

  rawBlocks.forEach((blockText) => {
    const blockData = parseBlockFileToObject(blockText);
    if (!blockData.ID) {
      options.onMissingBlockId?.(blockText);
      return;
    }

    blocksArray.push(helper._fromPlugin(name, blockData, parsePluginNesting(blockData), { force: options.force }));
  });

  return {
    name,
    kind: "category",
    contents: blocksArray,
  };
}
